
import React from 'react';

const GameWeapons = () => {
  const weapons = [
    {
      name: "Tactical Assault Rifle",
      category: "Primary",
      damage: 85,
      accuracy: 90,
      range: 80,
      description: "Standard-issue assault rifle for SWAT operations featuring high accuracy and versatility. Perfect for most combat situations with a balanced performance profile."
    },
    {
      name: "Combat Shotgun",
      category: "Primary",
      damage: 95,
      accuracy: 65,
      range: 40,
      description: "High-powered close-quarters weapon delivering devastating damage at short range. Ideal for room clearing and tight corridors."
    },
    {
      name: "Precision Sniper Rifle",
      category: "Primary",
      damage: 100,
      accuracy: 100,
      range: 100,
      description: "Long-range precision rifle for eliminating targets from a distance. One-shot capability with perfect accuracy when time is slowed."
    },
    {
      name: "Tactical SMG",
      category: "Primary",
      damage: 75,
      accuracy: 80,
      range: 60,
      description: "Rapid-fire submachine gun with excellent handling characteristics. High rate of fire compensates for lower damage per shot."
    },
    {
      name: "Combat Pistol",
      category: "Secondary",
      damage: 70,
      accuracy: 85,
      range: 60,
      description: "Standard sidearm with reliable performance characteristics. Fast draw time and decent stopping power make it a reliable backup weapon."
    },
    {
      name: "Heavy Revolver",
      category: "Secondary",
      damage: 90,
      accuracy: 90,
      range: 70,
      description: "High-caliber handgun with superior stopping power. Slower fire rate but delivers massive damage with each shot."
    },
    {
      name: "Stun Grenades",
      category: "Tactical",
      damage: 10,
      accuracy: "Area Effect",
      range: "Room-sized",
      description: "Non-lethal tactical grenades that temporarily blind and disorient enemies. Essential for hostage rescue missions where civilian safety is paramount."
    },
    {
      name: "Breaching Charges",
      category: "Equipment",
      damage: "Structure Only",
      accuracy: "Direct Placement",
      range: "Contact",
      description: "Specialized explosives for creating entry points through doors and walls. Provides tactical advantage through surprise entry."
    }
  ];

  // SVG for weapon stat bar
  const StatBar = ({ value, color }) => (
    <div className="w-full bg-gray-800 rounded-full h-2.5">
      <div 
        className={`h-2.5 rounded-full ${color}`} 
        style={{ width: `${value}%` }}
      ></div>
    </div>
  );

  // SVG weapon icon
  const WeaponIcon = ({ type }) => {
    switch(type) {
      case "Primary":
        return (
          <svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" className="w-8 h-8">
            <rect x="5" y="15" width="30" height="5" rx="1" fill="#8B5CF6"/>
            <rect x="10" y="10" width="3" height="15" rx="1" fill="#7E69AB"/>
            <rect x="32" y="16" width="5" height="3" fill="#7E69AB"/>
          </svg>
        );
      case "Secondary":
        return (
          <svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" className="w-8 h-8">
            <rect x="10" y="15" width="15" height="5" rx="1" fill="#8B5CF6"/>
            <rect x="10" y="20" width="5" height="8" rx="1" fill="#7E69AB"/>
          </svg>
        );
      case "Tactical":
        return (
          <svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" className="w-8 h-8">
            <circle cx="20" cy="20" r="10" fill="#8B5CF6"/>
            <path d="M20 10 L20 14 M16 12 L24 12" stroke="#7E69AB" strokeWidth="2"/>
          </svg>
        );
      case "Equipment":
        return (
          <svg viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" className="w-8 h-8">
            <rect x="10" y="10" width="20" height="20" rx="2" fill="#8B5CF6"/>
            <path d="M15 20 L25 20 M20 15 L20 25" stroke="#1A1F2C" strokeWidth="2"/>
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <section className="my-16" id="weapons">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-3xl font-bold mb-4 text-center text-purple-300">Arsenal & Equipment</h2>
        <p className="text-lg text-center mb-10 text-indigo-100">
          Time Shooter 3 Unblocked features an extensive arsenal of weapons and tactical equipment. Master each one to become the ultimate SWAT operative.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {weapons.map((weapon, index) => (
            <div key={index} className="bg-indigo-900/30 rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300">
              <div className="bg-indigo-800/50 p-4 flex items-center">
                <div className="bg-purple-900/50 p-2 rounded-md mr-4">
                  <WeaponIcon type={weapon.category} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-purple-300">{weapon.name}</h3>
                  <span className="text-sm text-indigo-200">{weapon.category}</span>
                </div>
              </div>
              
              <div className="p-6">
                <p className="mb-4 text-indigo-100">{weapon.description}</p>
                
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-indigo-300">Damage</span>
                      <span className="text-sm text-indigo-300">{typeof weapon.damage === 'number' ? `${weapon.damage}%` : weapon.damage}</span>
                    </div>
                    {typeof weapon.damage === 'number' ? (
                      <StatBar value={weapon.damage} color="bg-red-600" />
                    ) : (
                      <div className="italic text-sm text-indigo-200">Special</div>
                    )}
                  </div>
                  
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-indigo-300">Accuracy</span>
                      <span className="text-sm text-indigo-300">{typeof weapon.accuracy === 'number' ? `${weapon.accuracy}%` : weapon.accuracy}</span>
                    </div>
                    {typeof weapon.accuracy === 'number' ? (
                      <StatBar value={weapon.accuracy} color="bg-green-600" />
                    ) : (
                      <div className="italic text-sm text-indigo-200">Special</div>
                    )}
                  </div>
                  
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-indigo-300">Range</span>
                      <span className="text-sm text-indigo-300">{typeof weapon.range === 'number' ? `${weapon.range}%` : weapon.range}</span>
                    </div>
                    {typeof weapon.range === 'number' ? (
                      <StatBar value={weapon.range} color="bg-blue-600" />
                    ) : (
                      <div className="italic text-sm text-indigo-200">Special</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-gradient-to-r from-indigo-900/50 to-purple-900/50 p-8 rounded-lg text-center">
          <h3 className="text-2xl font-bold mb-4 text-purple-300">Weapon Customization</h3>
          <p className="text-lg mb-6 text-indigo-100">
            In Time Shooter 3 Unblocked, many weapons can be customized with attachments like scopes, suppressors, and extended magazines. Complete missions to unlock more weapons and attachments for your arsenal!
          </p>
          
          {/* SVG customization illustration */}
          <div className="flex justify-center mb-6">
            <svg viewBox="0 0 300 100" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-md">
              <rect x="50" y="40" width="200" height="20" rx="2" fill="#1A1F2C" stroke="#7E69AB" strokeWidth="2"/>
              <rect x="70" y="35" width="20" height="30" rx="1" fill="#9b87f5"/>
              <rect x="230" y="45" width="30" height="10" rx="1" fill="#8B5CF6"/>
              <rect x="120" y="30" width="15" height="15" rx="2" fill="#6E59A5"/>
              <rect x="160" y="30" width="30" height="10" fill="#D6BCFA" fillOpacity="0.3"/>
              <circle cx="170" cy="35" r="3" fill="#D6BCFA"/>
              <text x="150" y="80" fontSize="12" fill="#D6BCFA" textAnchor="middle">Attachments Customize Your Weapons</text>
            </svg>
          </div>
          
          <div className="flex justify-center">
            <a href="#play-now" className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-8 rounded-full transition-all transform hover:scale-105">
              Try Different Weapons Now
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GameWeapons;
